
import { User } from "@/context/AuthContext";

// Define the AuthService types
export interface AuthResponse {
  user: User | null;
  error?: string;
}

export interface OtpResponse {
  success: boolean;
  error?: string;
}

/**
 * AuthService - Handles all authentication related operations
 * Supporting both password and OTP based authentication
 */
class AuthService {
  // Login with email and password
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation - in a real app, this would be handled by the backend
      if (!email.includes("@") || password.length < 6) {
        return { user: null, error: "Invalid credentials" };
      }
      
      // Create a mock user based on the email
      const mockUser: User = {
        id: Math.random().toString(36).substring(2, 15),
        name: email.split("@")[0],
        email,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        role: "user",
        provider: "email"
      };
      
      // Save user to localStorage to persist session
      localStorage.setItem("anzir_user", JSON.stringify(mockUser));
      return { user: mockUser };
    } catch (error) {
      console.error("Login error:", error);
      return { user: null, error: "Failed to login. Please try again." };
    }
  }

  // Send OTP to user's email
  async sendOtp(email: string): Promise<OtpResponse> {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation - in a real app, this would be handled by the backend
      if (!email.includes("@")) {
        return { success: false, error: "Invalid email" };
      }
      
      // Simulate sending OTP - in real world, backend would generate and send OTP
      console.log(`OTP sent to ${email}`);
      
      // Store the email and a mock OTP in localStorage for demo purposes
      // In a real app, OTP would be generated and validated on the backend
      localStorage.setItem("anzir_otp_email", email);
      localStorage.setItem("anzir_otp", "123456"); // This is just for demo, never store OTPs client-side in production
      
      return { success: true };
    } catch (error) {
      console.error("Send OTP error:", error);
      return { success: false, error: "Failed to send OTP. Please try again." };
    }
  }

  // Login with OTP
  async loginWithOtp(email: string, otp: string): Promise<AuthResponse> {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation - in a real app, this would be handled by the backend
      if (!email.includes("@")) {
        return { user: null, error: "Invalid email" };
      }
      
      // For demo purposes, validate against mock OTP
      // In a real app, this validation would happen on the backend
      const storedEmail = localStorage.getItem("anzir_otp_email");
      const storedOtp = localStorage.getItem("anzir_otp");
      
      if (email !== storedEmail || otp !== storedOtp) {
        return { user: null, error: "Invalid OTP" };
      }
      
      // Create a mock user based on the email
      const mockUser: User = {
        id: Math.random().toString(36).substring(2, 15),
        name: email.split("@")[0],
        email,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        role: "user",
        provider: "email"
      };
      
      // Save user to localStorage to persist session
      localStorage.setItem("anzir_user", JSON.stringify(mockUser));
      
      // Clean up OTP data
      localStorage.removeItem("anzir_otp_email");
      localStorage.removeItem("anzir_otp");
      
      return { user: mockUser };
    } catch (error) {
      console.error("OTP login error:", error);
      return { user: null, error: "Failed to login with OTP. Please try again." };
    }
  }

  // Register new user
  async register(name: string, email: string, password: string): Promise<AuthResponse> {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation - in a real app, this would be handled by the backend
      if (!email.includes("@") || password.length < 6) {
        return { user: null, error: "Invalid credentials" };
      }
      
      // Create a mock user
      const mockUser: User = {
        id: Math.random().toString(36).substring(2, 15),
        name,
        email,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        role: "user",
        provider: "email"
      };
      
      // Save user to localStorage to persist session
      localStorage.setItem("anzir_user", JSON.stringify(mockUser));
      return { user: mockUser };
    } catch (error) {
      console.error("Registration error:", error);
      return { user: null, error: "Failed to register. Please try again." };
    }
  }

  // Login with provider (Google, Apple, Facebook)
  async loginWithProvider(provider: "google" | "apple" | "facebook"): Promise<AuthResponse> {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate random email and name based on provider
      const randomId = Math.random().toString(36).substring(2, 8);
      const email = `user_${randomId}@${provider}.com`;
      const name = `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`;
      
      // Create a mock user with provider info
      const mockUser: User = {
        id: Math.random().toString(36).substring(2, 15),
        name,
        email,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        role: "user",
        provider,
      };
      
      // Save user to localStorage to persist session
      localStorage.setItem("anzir_user", JSON.stringify(mockUser));
      return { user: mockUser };
    } catch (error) {
      console.error(`${provider} login error:`, error);
      return { user: null, error: `Failed to login with ${provider}. Please try again.` };
    }
  }

  // Reset password functionality
  async resetPassword(email: string): Promise<OtpResponse> {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, this would send a reset email
      if (!email.includes("@")) {
        return { success: false, error: "Invalid email" };
      }
      
      // No need to update state, just simulate success
      console.log(`Password reset email sent to ${email}`);
      return { success: true };
    } catch (error) {
      console.error("Password reset error:", error);
      return { success: false, error: "Failed to reset password. Please try again." };
    }
  }

  // Logout user
  logout(): void {
    localStorage.removeItem("anzir_user");
  }

  // Get current user from localStorage
  getCurrentUser(): User | null {
    const storedUser = localStorage.getItem("anzir_user");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (error) {
        console.error("Failed to parse stored user:", error);
        localStorage.removeItem("anzir_user");
      }
    }
    return null;
  }
}

export const authService = new AuthService();
export default authService;
